<template>
    <header class="text-center mb-4">
        <h2 class="skin-page-heading">Equity Release</h2>
    </header>

    <div class="row">
        <div class="col-xxl-10 mx-auto">
            <p><strong>What is Equity Release?</strong></p>
            <p>If you are {{ minEligibleAge }} or over, equity release is simply a way of unlocking the value of your property and turning it into tax-free cash. Think of it as a loan that's repaid using your home once you pass away or require long-term care. Equity release is often taken through what's called a lifetime mortgage.</p>
            <p>Many people who want to access some equity in their home to support their retirement income don't want to sell and downsize. You don't have to sell your home or move out with a lifetime mortgage, but it can provide additional regular payments or a lump sum to support your retirement income.</p>
            <p>To be clear taking out equity release will reduce the amount of inheritance you leave behind.</p>

            <p class="mt-4"><strong>Advice</strong></p>
            <p>You must speak with a specialist equity release adviser before taking out equity release. It's a big decision, and you need to be aware of all the facts so you can decide if it's right for you.</p>
            <p>We're not encouraging borrowing, or saying it's the right solution, we're just helping you to understand your options.</p>

            <template v-if="!isOfEligibleAge">
                <div class="alert alert-warning mb-4" role="alert">
                    <h4 class="alert-heading">
                        <i class="bi bi-exclamation-circle text-muted fs-5 me-1"></i>
                        Age warning
                    </h4>
                    <p class="mb-0">
                        Equity release is not available until you are at least {{ minEligibleAge }}. The calculate button will show the equity that could be released for someone who is age {{ minEligibleAge }}.
                    </p>
                </div>
            </template>
            <template v-else>
                <div class="alert alert-warning mb-0" role="alert">
                    <h4 class="alert-heading">
                        <i class="bi bi-exclamation-circle text-muted fs-5 me-1"></i>
                        Joint home ownership
                    </h4>
                    <p class="mb-0">
                        Do you own your house jointly with someone under the age of {{ minEligibleAge }}? If you do, you won't be able to take out equity release until both home owners are over {{ minEligibleAge }}.
                    </p>
                </div>
                <p class="mt-3" v-if="hasIncludedEquityReleaseInPlan">You can use the calculator below to see whether it could be possible to borrow the {{ formatter.formatCurrency(plannedEquityReleaseAmount) }} you have included in your plan.</p>
                <p class="mt-3" v-else>You haven't added any to your plan at the moment, but let's see what it may be possible to borrow.</p>
            </template>

            <div class="card bg-light col-xl-6">
                <div class="card-body">
                    <p>What is your home's current estimated value?</p>

                    <div class="form-floating mb-3">
                        <number-input ref="currentValueInput" v-model="currentValueModel" description="current value" :max="4000000"></number-input>
                    </div>

                    <button type="button" @click="onCalculatePotentialRelease" class="btn px-3 btn-secondary">
                        Calculate
                    </button>
                </div>
            </div>

            <div v-if="showCalcResult" class="mt-3">
                <p>Based on some high level calculations using current rates provided to us you may be able to release up to <strong>{{ fundsAvailable }}</strong> in equity {{ whenBorrowingMayOccur }}, or perhaps more at another later date.</p>
                <p>The figure above assumes you have no remaining mortgage on your home. You can get more accurate figures considering payments needed to clear any outstanding mortgage from any adviser you choose to take advice with.</p>
                <p>We do not receive any referral fees if you decide to take advice from any adviser.</p>
            </div>

            <p class="mt-4"><img src="/Content/images/Providers/lv-logo.png" width="160" alt="LV= Logo" /></p>
            <p class="mt-3"><strong>Where can I get this advice?</strong></p>
            <p>To advise you on whether equity release may be right for you and which type is best for your circumstances, we've partnered with LV=.</p>
            <p>They can provide you with a whole of market Equity Release advice service. Everything they do at LV= is focused on giving you honest advice and finding you a suitable Equity Release plan with a fast and friendly service.</p>
            <p>They can also help with wider advice around your whole retirement plans if needed.</p>

            <p class="mt-4"><strong>Want to learn more?</strong></p>
            <p>To <a href="https://www.lv.com/mkt/guiide?utm_source=guiide&utm_medium=website&utm_campaign=guiide_referral&utm_content=guiide_p_one" target="_blank">find out how much equity you could release</a> call the experts at LV= today.</p>
            <p>0800 023 2956 - Lines open: 9am to 5pm Monday to Friday</p>
            <p>TextDirect: First dial 18001</p>
            <p>LV= may record and / or monitor calls for training and audit purposes.</p>

            <p class="mt-5">
                <small>
                    Disclaimer - Please note we do not receive any referral fees or commission from LV should you choose to take advice from them. Like some of our other partners, they
                    license our technology so we do have other commercial arrangements with them.
                </small>
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import NumberInput from "../common/NumberInput.vue";
    import { EquityReleaseRateResponse } from "@/guiide/ts/response/equityReleaseRateResponse";
    import { formatter } from "@/db/ts/formatter";
    import HttpStatusCode from "@/shared/ts/httpStatusCode";
    import { app } from "@/guiide/ts/app";
    import { getAgeNow, round } from "@/guiide/ts/utilities";
    import { useStore } from "vuex";
    import CashType from "@/guiide/ts/cashType";
    import Monitoring from "@/guiide/ts/monitoring/monitoring";

    const store = useStore();
    const minEligibleAge = 55;
    const currentValueInput = ref(null);
    const currentValueModel = ref<number | null>(null);
    const percentage = ref<number | null>(null);
    const fundsAvailable = ref<string>("");
    const showCalcResult = ref<boolean>(false);

    const currentAge = computed<number | null>(() => getAgeNow());
    const isOfEligibleAge = computed<boolean>(() => currentAge.value != null && currentAge.value > minEligibleAge);

    const plannedEquityReleaseAmount = computed<number>(() => {
        const cash = store.state.retiree?.otherCash;
        if (cash == null || cash.length == 0) {
            return 0;
        }

        const equityReleaseCashItems = cash.filter(c => c.cashType == CashType.EquityRelease);

        if (equityReleaseCashItems.length == 0) {
            return 0;
        }

        return equityReleaseCashItems[0].amount;
    });

    const hasIncludedEquityReleaseInPlan = computed<boolean>(() => plannedEquityReleaseAmount.value > 0);

    const whenBorrowingMayOccur = computed<string>(() => {
        return isOfEligibleAge.value ? "currently" : `at age ${minEligibleAge}`;
    });

    async function onCalculatePotentialRelease() {
        if (currentAge.value == null) {
            return;
        }

        if (!currentValueInput.value.isValid()) {
            return;
        }

        if (percentage.value != null) {
            updateCalculatedValue();
            return;
        }

        const ageToUse = Math.max(minEligibleAge, currentAge.value);
        const rateResponse: EquityReleaseRateResponse = await app.getEquityReleaseRate(ageToUse);

        if (rateResponse.status === HttpStatusCode.Ok) {
            percentage.value = rateResponse.percentage;
            updateCalculatedValue();
            showCalcResult.value = true;
        }
    }

    function updateCalculatedValue() {
        const amount = round(currentValueModel.value * percentage.value, 100);
        fundsAvailable.value = formatter.formatCurrency(amount);
    }
</script>
